import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n';
import { mergeContent } from './siteContent';
import { localizeShots, shotUrl } from './shots';
import LEGAL from './legalDefaults.json';

/*
 * Content for the marketing sub-pages. Defaults come from the existing i18n
 * texts (so all six languages keep working); anything saved in
 * Admin → Landingspagina → <page> overrides them per language.
 * "*words*" renders in the brand green.
 */

const arr = (t, key) => {
  const v = t(key, { returnObjects: true });
  return Array.isArray(v) ? v : [];
};

/* Highlight the first `n` words in green (used where the design colours the opening words). */
const lead = (text, n = 1) => {
  const words = String(text || '').split(' ');
  if (words.length <= n) return text;
  return `*${words.slice(0, n).join(' ')}* ${words.slice(n).join(' ')}`;
};

const FEATURES_INTRO = {
  nl: '*Alles wat je* nodig hebt om inzicht te krijgen in je winst',
  en: '*Everything* you need to understand your profit',
  fr: '*Tout ce qu’il vous faut* pour comprendre votre bénéfice',
  de: '*Alles, was Sie brauchen,* um Ihren Gewinn zu verstehen',
  es: '*Todo lo que necesitas* para entender tu beneficio',
  zh: '*洞察利润*所需的一切',
};

const bannerDefaults = (t, prefix) => ({
  title: t(`${prefix}.cta_title`),
  subtitle: t(`${prefix}.cta_text`),
  primary_label: t('site.start_free'),
  secondary_label: t('home.cta_secondary'),
  secondary_link: '/contact',
});

const legalDefaults = (key) => (t) => ({
  hero: {
    kicker: t('site.footer_legal'),
    title: LEGAL[key].title,
    subtitle: LEGAL[key].subtitle,
    updated: `Laatst bijgewerkt: ${LEGAL[key].updated}`,
  },
  notice: t('legal.dutch_only'),
  sections: LEGAL[key].sections,
});

export const PAGE_DEFAULTS = {
  features: (t, lang) => ({
    hero: {
      kicker: t('features_page.badge'),
      title: lang === 'nl' ? '*Alles wat je* nodig hebt voor WooCommerce-winstoptimalisatie' : lead(t('features_page.title'), 2),
      subtitle: t('features_page.intro'),
      cta_label: t('site.start_free'),
      cta_link: 'register',
    },
    intro: { title: FEATURES_INTRO[lang] || FEATURES_INTRO.en },
    cards: arr(t, 'features_page.sections').map((s, i) => ({
      icon: ['chart', 'trend', 'pl', 'box', 'ai'][i % 5],
      kicker: s.tag,
      title: s.title,
      body: s.text,
      cta_label: t('site.start_free'),
      cta_link: 'register',
      image_url: shotUrl(['overzicht', 'verkopen', 'financieel', 'producten', 'ai'][i % 5], lang),
      image_fit: 'cover',
    })),
    boxes: {
      title: lang === 'nl' ? '*Meer onder* de motorkap' : lead(t('features_page.supporting_title'), 2),
      items: arr(t, 'features_page.supporting').map((s, i) => ({
        icon: ['globe', 'invoice', 'shield'][i % 3],
        title: s.title,
        text: s.description,
      })),
    },
    cta: bannerDefaults(t, 'features_page'),
  }),

  how: (t, lang) => {
    const shots = ['overzicht', 'verkopen', 'financieel', 'producten', 'prijsradar', 'prijsradar'];
    return {
      hero: {
        kicker: t('how_it_works.badge'),
        title: lead(t('how_it_works.title'), 2),
        subtitle: t('how_it_works.intro'),
        cta_label: t('site.start_free'),
        cta_link: 'register',
      },
      steps: {
        step_label: t('how_it_works.step_label'),
        items: arr(t, 'how_it_works.steps').map((s, i) => ({
          title: s.title,
          body: s.text,
          tips: Array.isArray(s.tips) ? s.tips : [],
          image_url: shotUrl(shots[i % shots.length], lang),
        })),
      },
      extras: {
        kicker: t('how_it_works.extras_eyebrow'),
        title: lead(t('how_it_works.extras_title'), 1),
        subtitle: t('how_it_works.extras_intro'),
        items: arr(t, 'how_it_works.extras').map((s, i) => ({
          icon: ['bell', 'globe', 'spreadsheet', 'card', 'shield', 'users'][i % 6],
          title: s.title,
          text: s.text,
        })),
      },
      cta: {
        title: t('how_it_works.cta_title'),
        subtitle: t('how_it_works.cta_text'),
        primary_label: t('how_it_works.cta_primary'),
        secondary_label: t('how_it_works.cta_secondary'),
        secondary_link: '/contact',
      },
    };
  },

  pricing: (t) => ({
    hero: {
      kicker: t('pricing_page.badge'),
      title: lead(t('pricing_page.title'), 1),
      subtitle: t('pricing_page.subtitle'),
    },
    cta: {
      title: t('home.cta_title'),
      subtitle: t('home.cta_subtitle'),
      primary_label: t('site.start_free'),
      secondary_label: t('home.cta_secondary'),
      secondary_link: '/contact',
    },
  }),

  about: (t) => ({
    hero: {
      kicker: t('about.badge'),
      title: lead(t('about.title'), 1),
      subtitle: t('about.intro'),
    },
    story: {
      items: [
        { icon: 'target', title: t('about.mission_title'), body: `${t('about.mission_p1')}\n${t('about.mission_p2')}` },
        { icon: 'map', title: t('about.why_title'), body: `${t('about.why_p1')}\n${t('about.why_p2')}` },
      ],
    },
    team: {
      title: lead(t('about.team_title'), 1),
      subtitle: t('about.team_intro'),
      members: arr(t, 'about.founders').map((f, i) => ({
        name: ['Heino Schietecatte', 'Glenn Vanlerberghe'][i] || '',
        role: f.role,
        bio: f.bio,
        photo: ['/team/heino-schietecatte.webp', '/team/glenn-vanlerberghe.webp'][i] || '',
      })),
    },
    values: {
      title: lead(t('about.values_title'), 1),
      items: arr(t, 'about.values').map((v, i) => ({ icon: ['target', 'shield', 'heart'][i % 3], title: v.title, text: v.text })),
    },
    cta: {
      title: t('about.cta_title'),
      subtitle: t('about.cta_text'),
      primary_label: t('about.cta_button'),
      primary_link: '/contact',
      secondary_label: t('site.start_free'),
      secondary_link: 'register',
    },
  }),

  contact: (t) => ({
    hero: {
      kicker: t('contact_page.badge'),
      title: lead(t('contact_page.title'), 1),
      subtitle: t('contact_page.intro'),
    },
    info: {
      email_label: t('contact_page.email_label'),
      email: 'info@wowprofit.io',
      response_label: t('contact_page.response_label'),
      response_value: t('contact_page.response_value'),
      office_label: t('contact_page.office_label'),
      office_value: t('contact_page.office_value'),
    },
    form: {
      name: t('contact_page.form_name'),
      email: t('contact_page.form_email'),
      subject: t('contact_page.form_subject'),
      message: t('contact_page.form_message'),
      submit: t('contact_page.form_submit'),
      sending: t('contact_page.form_sending'),
      success: t('contact_page.success'),
      error: t('contact_page.error'),
    },
  }),

  faq: (t) => ({
    hero: {
      kicker: 'FAQ',
      title: lead(t('landing_page_ui.faq_title'), 1),
      subtitle: t('faq_ui.cta_text'),
    },
    labels: {
      loading: t('faq_ui.loading'),
      disabled: t('faq_ui.disabled'),
    },
    cta: {
      title: t('faq_ui.cta_title'),
      subtitle: t('faq_ui.cta_text'),
      primary_label: t('faq_ui.cta_primary'),
      secondary_label: t('faq_ui.cta_secondary'),
      secondary_link: '/contact',
    },
  }),

  terms: legalDefaults('terms'),
  privacy: legalDefaults('privacy'),
  cookies: legalDefaults('cookies'),
};

export const pageDefaultsFor = (page, lang) => {
  const build = PAGE_DEFAULTS[page];
  return build ? build(i18n.getFixedT(lang), lang) : {};
};

const cache = {};

const fetchPage = (page, lang) => {
  const key = `${page}:${lang}`;
  if (!cache[key]) {
    cache[key] = axios
      .get(`/page-content/${page}?lang=${lang}`)
      .then((res) => res.data?.content || {})
      .catch(() => {
        delete cache[key];
        return {};
      });
  }
  return cache[key];
};

/* Resolved content for one sub-page in the current UI language. */
export const usePageContent = (page) => {
  const { i18n: inst } = useTranslation();
  const lang = (inst.language || 'nl').slice(0, 2);
  const [cms, setCms] = useState({});

  useEffect(() => {
    let alive = true;
    fetchPage(page, lang).then((c) => alive && setCms(c));
    return () => {
      alive = false;
    };
  }, [page, lang]);

  return { lang, cms, content: localizeShots(mergeContent(pageDefaultsFor(page, lang), cms), lang) };
};
